import React, { useState } from "react";
import { api, formatApiError } from "@/lib/http";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NotesRewrite } from "@/components/booking/NotesRewrite";
import { Pin, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

export const SNAPSHOT_FIELDS = [
  ["skin_type", "Skin type", "e.g. Fitzpatrick III, oily T-zone"],
  ["allergies", "Allergies / reactions", "e.g. redness 2 days after IPL, latex"],
  ["medical", "Medical", "e.g. PCOS, on isotretinoin until Mar"],
  ["preferred_staff", "Prefers", "e.g. Sarah, mornings"],
  ["do_nots", "Do nots", "e.g. no numbing cream, don't text before 9"],
  ["payment_notes", "Payments", "e.g. pays cash, has 6-pack laser"],
];

// Read-only chips — used in the calendar appointment panel.
export const SnapshotChips = ({ snapshot, compact = false }) => {
  const filled = SNAPSHOT_FIELDS.filter(([k]) => (snapshot?.[k] || "").trim());
  if (!filled.length && !(snapshot?.line || "").trim()) return null;
  return (
    <div className={`flex flex-wrap gap-1.5 ${compact ? "" : "mt-1"}`} data-testid="snapshot-chips">
      {filled.map(([k, label]) => (
        <span key={k} className={`inline-flex items-baseline gap-1 rounded-full border px-2.5 py-0.5 text-xs ${k === "allergies" || k === "medical" || k === "do_nots" ? "border-amber-300 bg-amber-50 text-amber-900" : "border-border bg-muted/40"}`} data-testid={`snapshot-chip-${k}`}>
          <span className="text-[10px] uppercase tracking-wide font-semibold opacity-70">{label}</span>{snapshot[k]}
        </span>
      ))}
      {(snapshot?.line || "").trim() && <span className="basis-full text-sm text-foreground/80 whitespace-pre-wrap" data-testid="snapshot-line">{snapshot.line}</span>}
    </div>
  );
};

// Pinned facts about the client, always visible at the top of the file. Replaces the old Notes + Medical history boxes.
export const ClientSnapshot = ({ client, onSaved }) => {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(client.snapshot || {});
  const [busy, setBusy] = useState(false);
  const empty = !SNAPSHOT_FIELDS.some(([k]) => (client.snapshot?.[k] || "").trim()) && !(client.snapshot?.line || "").trim();
  const save = async () => {
    setBusy(true);
    try { await api.put(`/clients/${client.id}`, { name: client.name, snapshot: { ...v, migrated_from_legacy: false } }); toast.success("Snapshot saved"); setEdit(false); onSaved?.(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border bg-card p-3" data-testid="client-snapshot">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Pin className="h-3.5 w-3.5" />Snapshot</div>
        {!edit && <button type="button" onClick={() => { setV(client.snapshot || {}); setEdit(true); }} className="text-xs text-primary inline-flex items-center gap-1 hover:underline" data-testid="snapshot-edit"><Pencil className="h-3 w-3" />{empty ? "Add facts" : "Edit"}</button>}
      </div>
      {!edit && (empty ? <p className="text-sm text-muted-foreground mt-1">Nothing pinned yet — skin type, allergies, preferences, do-nots.</p> : <SnapshotChips snapshot={client.snapshot} />)}
      {edit && (
        <div className="mt-2 space-y-2" data-testid="snapshot-editor">
          <div className="grid sm:grid-cols-2 gap-2">
            {SNAPSHOT_FIELDS.map(([k, label, ph]) => (
              <div key={k} className="space-y-0.5"><label className="text-[11px] text-muted-foreground">{label}</label><Input value={v[k] || ""} onChange={(e) => setV((s) => ({ ...s, [k]: e.target.value }))} placeholder={ph} className="h-9 rounded-lg text-sm" data-testid={`snapshot-input-${k}`} /></div>
            ))}
          </div>
          <div className="space-y-0.5"><label className="text-[11px] text-muted-foreground">Anything else (one short line)</label>
            <textarea value={v.line || ""} onChange={(e) => setV((s) => ({ ...s, line: e.target.value }))} rows={2} className="w-full rounded-lg border border-input bg-background p-2 text-sm" data-testid="snapshot-line-input" />
            <NotesRewrite clientId={client.id} value={v.line} onApply={(t) => setV((s) => ({ ...s, line: t }))} /></div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" className="rounded-lg" onClick={() => setEdit(false)} data-testid="snapshot-cancel"><X className="h-3.5 w-3.5 mr-1" />Cancel</Button>
            <Button type="button" size="sm" className="rounded-lg" onClick={save} disabled={busy} data-testid="snapshot-save"><Check className="h-3.5 w-3.5 mr-1" />Save</Button>
          </div>
        </div>
      )}
    </div>
  );
};
